import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { invitationService } from '@/services/InvitationService';
import { UserPlus, Heart, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  relation: Yup.string().required('Relation is required'),
  questions: Yup.array()
    .of(
      Yup.object({
        question: Yup.string().required('Question is required')
      })
    )
    .min(1, 'At least one question is required')
    .required('Questions are required'),
});

const InviteRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [token, setToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [existingQuestionsData, setExistingQuestionsData] = useState([]);
  const hasVerified = useRef(false); // Prevent duplicate verification calls

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (!urlToken) {
      toast.error('Invalid invitation link');
      navigate('/');
      return;
    }

    setToken(urlToken);
    
    // Only verify if we haven't already verified
    if (!hasVerified.current) {
      hasVerified.current = true;
      verifyToken(urlToken);
    }
  }, [searchParams, navigate]);

  const verifyToken = async (tokenToVerify) => {
    try {
      setIsVerifying(true);
      const response = await invitationService.verifyInviteeToken(tokenToVerify);
      
      console.log('Token verification response:', response);
      
      // Check if response indicates success
      if (response.message === "Token accepted successfully." || response.user) {
        setIsVerified(true);
        setUserInfo(response.user);
        
        // Handle existing questions data
        if (response.data && Array.isArray(response.data)) {
          setExistingQuestionsData(response.data);
          console.log('Found existing questions:', response.data);
        }
        
        // toast.success('Invitation verified successfully!');
      } else {
        throw new Error('Invalid token response');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      
      // Check if it's already expired/used token
      if (error.response?.data?.message === "Invalid or expired invitation.") {
        toast.error('This invitation link has already been used or has expired');
      } else {
        toast.error('Invalid or expired invitation link');
      }
      
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setIsVerifying(false);
    }
  };

  // Helper function to convert existing questions to formik format
  const prepareInitialQuestions = () => {
    if (existingQuestionsData.length > 0) {
      return existingQuestionsData.map((q) => ({
        question: q.question,
        id: q.id, // Only use actual backend IDs
        isExisting: true // Flag to identify backend questions
      }));
    }
    return [{ question: '' }]; // Default empty question without ID
  };

  // Get initial relation from existing data if available
  const getInitialRelation = () => {
    if (existingQuestionsData.length > 0 && existingQuestionsData[0].submitted_by_relation) {
      return existingQuestionsData[0].submitted_by_relation;
    }
    return '';
  };

  const formik = useFormik({
    initialValues: {
      name: userInfo?.name || '',
      relation: getInitialRelation(),
      questions: prepareInitialQuestions(),
    },
    enableReinitialize: true, // Allow formik to reinitialize when userInfo or existingQuestionsData changes
    validationSchema,
    onSubmit: async (values) => {
      console.log('Form values:', values);  
      // return;
      try {
        setIsSubmitting(true);
        
        // Process questions to include id for existing questions only
        const processedQuestions = values.questions
          .filter(q => q.question.trim() !== '') // Remove empty questions
          .map(q => {
            // Only include id for questions that actually exist in the backend
            if (q.id && q.isExisting) {
              return {
                id: q.id,
                question: q.question
              };
            } else {
              // New questions don't get an id
              return {
                question: q.question
              };
            }
          });
        
        const questionsData = {
          relation: values.relation,
          token: token,
          name: values.name,
          questions: processedQuestions
        };

        // Submit questions using the API
        const response = await invitationService.submitQuestions(questionsData);
        
        console.log('Questions submitted successfully:', questionsData);
        console.log('API Response:', response);
        toast.success('Thank you! Your questions have been submitted successfully.');
        
        // Redirect to thank you page after 2 seconds with user_name
        setTimeout(() => {
          navigate('/thank-you-invitee', { 
            state: { userName: response.user_name || response.data?.user_name || values.name } 
          });
        }, 2000);
      } catch (error) {
        console.error('Error submitting questions:', error);
        toast.error('Failed to submit questions. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Update formik values when existing questions data changes
  useEffect(() => {
    if (existingQuestionsData.length > 0 && userInfo) {
      const initialQuestions = prepareInitialQuestions();
      const initialRelation = getInitialRelation();
      formik.setFieldValue('questions', initialQuestions);
      if (initialRelation) {
        formik.setFieldValue('relation', initialRelation);
      }
    }
  }, [existingQuestionsData, userInfo]);

  const addQuestion = () => {
    const newQuestions = [...formik.values.questions, { question: '' }]; // No ID for new questions
    formik.setFieldValue('questions', newQuestions);
  };

  const removeQuestion = async (index) => {
    if (formik.values.questions.length > 1) {
      const questionToRemove = formik.values.questions[index];
      
      try {
        // Only call API for questions that actually exist in the backend
        if (questionToRemove.id && questionToRemove.isExisting) {
          await invitationService.deleteQuestion(questionToRemove.id);
          toast.success('Question deleted successfully');
        }
        
        // Update local state regardless of whether API was called
        const newQuestions = formik.values.questions.filter((_, i) => i !== index);
        formik.setFieldValue('questions', newQuestions);
      } catch (error) {
        console.error('Error deleting question:', error);
        toast.error('Failed to delete question. Please try again.');
      }
    }
  };

  const updateQuestion = (index, value) => {
    console.log('Updating question:', index, value);
    const newQuestions = [...formik.values.questions];
    newQuestions[index] = { ...newQuestions[index], question: value };
    console.log("newQuestions", newQuestions);
    console.log("newQuestions[index]", newQuestions[index]);
    formik.setFieldValue('questions', newQuestions);
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-[4px] mx-auto mb-4"></div>
          <p className="text-[18px] text-muted-foreground">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <UserPlus className="text-primary-foreground text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid Invitation</h1>
          <p className="text-[18px] text-muted-foreground">This invitation link is invalid or has expired.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center py-8">
      <div className="max-w-2xl mx-auto px-4 w-full">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-secondary rounded-xl border border-secondary-border p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <Heart className="text-primary-foreground text-2xl" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Submit Questions</h1>
            {userInfo && (
              <p className="text-primary font-medium mb-2 text-[18px]">
                Welcome, {userInfo.name}!
              </p>
            )}
            <p className="text-muted-foreground text-[18px]">
              Write meaningful questions that will be asked to gather thoughts and memories.
            </p>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="block text-[18px] font-semibold text-foreground mb-2">
                  Your Name <span className="text-destructive">*</span>
                </label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980] cursor-not-allowed" 
                  placeholder="Enter your full name" 
                  {...formik.getFieldProps('name')}
                  disabled={true}
                  readOnly={true}
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="text-destructive text-sm mt-1">{formik.errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-[18px] font-semibold text-foreground mb-2">
                  Your Relationship <span className="text-destructive">*</span>
                </label>
                <select 
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]"
                  {...formik.getFieldProps('relation')}
                >
                  <option value="" className="text-[#7C797980]">Select your relationship</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Brother">Brother</option>
                  <option value="Sister">Sister</option>
                  <option value="Uncle">Uncle</option>
                  <option value="Aunt">Aunt</option>
                  <option value="Cousin">Cousin</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </select>
                {formik.touched.relation && formik.errors.relation && (
                  <p className="text-destructive text-sm mt-1">{formik.errors.relation}</p>
                )}
              </div>

              <div className="bg-background rounded-[4px] border border-secondary-border p-6">
                <div className="mb-4">
                  <h3 className="text-[18px] font-semibold text-foreground">Submit Questions</h3>
                </div>
                
                <div className="space-y-4">
                  {formik.values.questions.map((questionObj, index) => {
                    const isExistingQuestion = questionObj.id && questionObj.isExisting;
                    return (
                    <div key={questionObj.id || `question-${index}`} className="relative">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <label className="block text-[18px] font-semibold text-foreground mb-2">
                            Question {index + 1} <span className="text-destructive">*</span>
                            {isExistingQuestion && (
                              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                                Previously submitted
                              </span>
                            )}
                          </label>
                          <textarea 
                            className="w-full px-4 py-3 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980] min-h-[120px] resize-none" 
                            placeholder="Type your question here..."
                            name={`questions.${index}.question`}
                            value={formik.values.questions[index]?.question || ''}
                            onChange={(e) => updateQuestion(index, e.target.value)}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.questions?.[index]?.question && formik.errors.questions?.[index]?.question && (
                            <p className="text-destructive text-sm mt-1">{formik.errors.questions[index].question}</p>
                          )}
                        </div>
                        {formik.values.questions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeQuestion(index)}
                            className="mt-8 p-2 text-destructive hover:bg-destructive/10 rounded-[4px] transition-colors"
                            title="Remove question"
                          >
                            <Trash2 className="text-base" />
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={addQuestion}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-background border border-secondary-border rounded-[4px] text-foreground hover:bg-secondary transition-colors text-[18px] font-semibold"
                  >
                    <Plus className="text-base" />
                    Add Question
                  </button>
                </div>

                {typeof formik.errors.questions === 'string' && (
                  <p className="text-destructive text-sm mt-2">{formik.errors.questions}</p>
                )}
              </div>

              <button 
                type="submit" 
                className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors text-[18px] font-semibold flex items-center justify-center gap-2"
                disabled={isSubmitting || !formik.isValid}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Submitting Questions...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="text-lg" />
                    <span>Submit Questions ({formik.values.questions.filter(q => q.question.trim() !== '').length})</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default InviteRegistration; 